import { create } from 'zustand';
import { withLogoSignedUrls } from 'services/organizationLogos';
import { EntityName } from 'shared/domain/entityName';
import { organizationClient } from 'shared/domain/organization/organizationClient';
import type { OrganizationModel } from 'shared/domain/organization/types/model';
import { createSelectors, StoreName, withDevtools } from '../config';
import { createEntityCollectionActions } from '../entityCollection';
import { createDataActions, createInitialData } from '../types';
import type { OrganizationsStore } from './types';

/** Organizations of the logged-in user, loaded and kept current through `organizationClient`. */
const useOrganizationsStoreBase = create<OrganizationsStore>()(
  withDevtools((set, get) => {
    const dataActions = createDataActions<OrganizationModel, OrganizationsStore>(set);
    // Logos are signed asynchronously; only the newest items are stored.
    let itemsVersion = 0;

    return {
      data: createInitialData<OrganizationModel>(),

      actions: {
        ...dataActions,
        ...createEntityCollectionActions(
          { client: organizationClient, entityName: EntityName.organizations },
          () => get().actions
        ),

        setItems: (items) => {
          const version = ++itemsVersion;
          void withLogoSignedUrls(items).then((itemsWithLogos) => {
            if (version === itemsVersion) {
              dataActions.setItems(itemsWithLogos);
            }
          });
        },
        reset: () => {
          itemsVersion += 1;
          dataActions.reset();
        },
        refresh: () => {
          get().actions.setLoading(true);
          organizationClient.sync();
        },
      },
    };
  }, StoreName.Organizations)
);

export const useOrganizationsStore = createSelectors(useOrganizationsStoreBase);

export const getOrganizationsStoreState = (): OrganizationsStore => useOrganizationsStore.getState();
